import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const COLORS = { too_expensive: '#ef4444', not_using: '#f59e0b', missing_feature: '#8b5cf6', switching: '#0ea5e9', just_testing: '#a8a29e', other: '#64748b', unspecified: '#d6d3d1' };
const MonthlyChart = ({ monthly }) => (
  <div className="mt-5" data-testid="cancellation-monthly-chart">
    <div className="text-xs font-semibold text-stone-600 mb-2">Cancellations by month (last 12 months)</div>
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={monthly}>
        <XAxis dataKey="month" tick={{ fontSize: 10 }} tickFormatter={(m) => ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(String(m).slice(5, 7)) - 1] || m} />
        <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={24} />
        <Tooltip />
        <Legend wrapperStyle={{ fontSize: 10 }} formatter={(k) => LABELS[k] || k} />
        {Object.keys(COLORS).map((k) => <Bar key={k} dataKey={k} stackId="r" fill={COLORS[k]} name={k} />)}
      </BarChart>
    </ResponsiveContainer>
  </div>
);
const ByPlan = ({ rows }) => (
  <div className="mt-5" data-testid="cancellation-by-plan">
    <div className="text-xs font-semibold text-stone-600 mb-2">By plan</div>
    {rows.length === 0 ? <div className="text-xs text-stone-400">No cancellations yet</div> : rows.map((p) => (
      <div key={p.plan_code} className="mb-2" data-testid={`cancellation-plan-${p.plan_code}`}>
        <div className="flex justify-between text-xs"><span className="font-semibold capitalize">{p.plan_code.replace(/_/g, ' ')}</span><span className="font-mono">{p.total}</span></div>
        <div className="flex h-2 rounded-full overflow-hidden bg-stone-100 mt-1">
          {Object.entries(p.reasons).map(([k, v]) => <div key={k} title={`${LABELS[k] || k}: ${v}`} style={{ width: `${(v / p.total) * 100}%`, background: COLORS[k] || '#a8a29e' }} />)}
        </div>
      </div>
    ))}
  </div>
);

const LABELS = {
  too_expensive: 'Too expensive', not_using: 'Not using it enough', missing_feature: 'Missing a feature',
  switching: 'Switching tools', just_testing: 'Just testing', other: 'Other', unspecified: 'No reason given',
};

export const CancellationInsights = () => {
  const [d, setD] = useState(null);
  useEffect(() => {
    axios.get(`${API}/billing/admin/cancellation-insights`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then((r) => setD(r.data)).catch(() => setD(false));
  }, []);
  if (d === null) return <div className="bg-white border border-stone-200 rounded-xl p-4 h-32 animate-pulse" data-testid="cancellation-insights-loading" />;
  if (d === false) return <div className="bg-white border border-stone-200 rounded-xl p-4 text-xs text-stone-500" data-testid="cancellation-insights-error">Cancellation insights are unavailable right now.</div>;
  const max = Math.max(1, ...Object.values(d.counts));
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-4" data-testid="cancellation-insights">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-sm font-bold text-stone-900">Why users cancel</h3>
        <div className="text-xs text-stone-500">
          <span data-testid="cancellation-total">{d.total_cancellations}</span> cancelled · <span className="text-violet-700 font-semibold" data-testid="cancellation-winbacks">{d.winbacks_accepted}</span> saved by offer
        </div>
      </div>
      <div className="space-y-1.5">
        {Object.entries(d.counts).filter(([k, v]) => v > 0 || k !== 'unspecified').map(([k, v]) => (
          <div key={k} className="flex items-center gap-2 text-xs" data-testid={`cancellation-reason-${k}`}>
            <span className="w-36 text-stone-600 shrink-0">{LABELS[k] || k}</span>
            <div className="flex-1 h-2 rounded-full bg-stone-100 overflow-hidden"><div className="h-full bg-red-400 transition-[width] duration-500" style={{ width: `${(v / max) * 100}%` }} /></div>
            <span className="w-6 text-right font-mono font-semibold">{v}</span>
          </div>
        ))}
      </div>
      <ByPlan rows={d.by_plan || []} />
      <MonthlyChart monthly={d.monthly || []} />
      {d.recent_notes.length > 0 && (
        <div className="mt-4 border-t border-stone-100 pt-3 space-y-2" data-testid="cancellation-notes">
          {d.recent_notes.map((n, i) => (
            <div key={i} className="text-xs"><span className="font-semibold text-stone-800">{n.name || n.email}</span>
              <span className="text-stone-400"> · {LABELS[n.reason] || n.reason} · {String(n.created_at).slice(0, 10)}</span>
              <div className="text-stone-600 italic">“{n.note}”</div></div>
          ))}
        </div>
      )}
    </div>
  );
};
